// frontend-dash/src/pages/AdminGroups.js
import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import {
    TeamIcon as Groups,
    AddIcon as Add,
    EditIcon as Edit,
    DeleteIcon as Delete,
    PersonIcon as Person,
    SearchIcon as Search,
    CloseIcon as X,
    CalendarIcon as Calendar,
    LocationIcon as Location,
    ClockIcon as Clock,
    ImageIcon as Image,
    UploadIcon as Upload,
    EmailIcon as Email,
    GlobeIcon as Globe
} from '../components/Icons';
import { HelpCircle } from 'lucide-react';
import MicroTour from '../components/MicroTour';
import GroupColorPicker from '../components/GroupColorPicker';

const STUFEN_PRESETS = [
    { name: 'Meute', color: '#f57c00', description: 'Für Kinder von 6 bis 10 Jahren (Altersspanne wird über das aktuelle Datum dynamisch berechnet)' },
    { name: 'Sippe', color: '#1976d2', description: 'Für Jugendliche (mit optionalem individuellem Jahrgangsbereich)' },
    { name: 'Sonstiges', color: '#455a64', description: 'Weitere Gruppen, Runden & Kreise' }
];

export const getGroupAgeInfo = (groupOrData) => {
    if (!groupOrData) return null;
    const currentYear = new Date().getFullYear();
    const stufe = (groupOrData.altersstufe || '').trim();

    if (stufe.toLowerCase() === 'meute' || stufe.toLowerCase() === 'wölflinge') {
        const start = currentYear - 10;
        const end = currentYear - 6;
        return {
            isMeute: true,
            ageLabel: '6–10 Jahre',
            birthYearsLabel: `Jahrgänge ${start}–${end}`,
            badge: `6–10 Jahre (${start}–${end})`
        };
    }

    const yStart = groupOrData.birthYearStart ? parseInt(groupOrData.birthYearStart) : null;
    const yEnd = groupOrData.birthYearEnd ? parseInt(groupOrData.birthYearEnd) : null;

    if (yStart && yEnd) {
        const minYear = Math.min(yStart, yEnd);
        const maxYear = Math.max(yStart, yEnd);
        const minAge = currentYear - maxYear;
        const maxAge = currentYear - minYear;
        return {
            isMeute: false,
            ageLabel: `ca. ${minAge}–${maxAge} Jahre`,
            birthYearsLabel: `Jahrgänge ${minYear}–${maxYear}`,
            badge: `ca. ${minAge}–${maxAge} Jahre (${minYear}–${maxYear})`
        };
    } else if (yStart) {
        const age = currentYear - yStart;
        return {
            isMeute: false,
            ageLabel: `ca. ${age} Jahre`,
            birthYearsLabel: `Jahrgang ${yStart}`,
            badge: `ca. ${age} Jahre (${yStart})`
        };
    }

    return null;
};

const getMainUrl = () => {
    if (process.env.REACT_APP_MAIN_URL) return process.env.REACT_APP_MAIN_URL;
    if (typeof window !== 'undefined' && window.location.hostname) {
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            return 'http://localhost:3000';
        }
        return `${window.location.protocol}//${window.location.hostname.replace(/^dash\./, '')}`;
    }
    return '';
};

const AdminGroups = () => {
    const { API_URL, user } = useContext(AuthContext);
    const [groups, setGroups] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [showModal, setShowModal] = useState(false);
    const [showMembersModal, setShowMembersModal] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedMemberIds, setSelectedMemberIds] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [isGalleryUploading, setIsGalleryUploading] = useState(false);
    const [galleryUrlInput, setGalleryUrlInput] = useState('');
    const [highlightInput, setHighlightInput] = useState('');
    const fileInputRef = useRef(null);
    const galleryInputRef = useRef(null);

    const filteredUsers = (users || []).filter(u => {
        if (!searchTerm) return true;
        const name = `${u.userData?.scoutName || ''} ${u.userData?.firstName || ''} ${u.userData?.lastName || ''} ${u.username || ''} ${u.email || ''}`.toLowerCase();
        return name.includes(searchTerm.toLowerCase());
    });

    const addMemberToGroup = async (userId) => {
        if (!selectedGroup) return;
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            const { data } = await axios.post(`${API_URL}/groups/${selectedGroup._id}/members`, { userIds: [userId] }, config);
            setSelectedGroup(data);
            fetchGroups();
            setMessage({ type: 'success', text: 'Mitglied hinzugefügt!' });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.message || 'Fehler beim Hinzufügen des Mitglieds' });
        }
    };

    const addBatchMembersToGroup = async () => {
        if (!selectedGroup || selectedMemberIds.length === 0) return;
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            const { data } = await axios.post(`${API_URL}/groups/${selectedGroup._id}/members`, { userIds: selectedMemberIds }, config);
            setSelectedGroup(data);
            setSelectedMemberIds([]);
            fetchGroups();
            setMessage({ type: 'success', text: `${selectedMemberIds.length} Mitglieder hinzugefügt!` });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.message || 'Fehler beim Hinzufügen der Mitglieder' });
        }
    };

    const removeMemberFromGroup = async (userId) => {
        if (!selectedGroup) return;
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            const { data } = await axios.delete(`${API_URL}/groups/${selectedGroup._id}/members/${userId}`, config);
            setSelectedGroup(data);
            fetchGroups();
            setMessage({ type: 'success', text: 'Mitglied entfernt!' });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.message || 'Fehler beim Entfernen des Mitglieds' });
        }
    };

    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        altersstufe: 'Meute',
        birthYearStart: '',
        birthYearEnd: '',
        primaryContactLeader: '',
        motto: '',
        logoUrl: '',
        description: '',
        longDescription: '',
        images: [],
        highlights: [],
        color: '#f57c00',
        leaders: [],
        hasSchedule: false,
        standardSchedule: {
            dayOfWeek: 5,
            startTime: '',
            endTime: '',
            location: ''
        },
        contactEmail: '',
        order: 0,
        isActive: true,
        isArchived: false,
        glowInTheDark: false
    });

    const [archiveFilter, setArchiveFilter] = useState('active'); // 'active' | 'archived' | 'all'
    const [showMicroTour, setShowMicroTour] = useState(false);

    const altersstufen = ['Meute', 'Sippe', 'Sonstiges'];
    const weekDays = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

    useEffect(() => {
        fetchGroups();
        fetchUsers();
        // eslint-disable-next-line
    }, []);

    const fetchGroups = async () => {
        try {
            const config = user?.token ? { headers: { Authorization: `Bearer ${user.token}` } } : {};
            const { data } = await axios.get(`${API_URL}/groups?includeArchived=true`, config);
            setGroups(Array.isArray(data) ? data : []);
            setLoading(false);
        } catch (error) {
            setMessage({ type: 'error', text: 'Fehler beim Laden der Gruppen' });
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            const { data } = await axios.get(`${API_URL}/users`, config);
            setUsers(data);
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsUploading(true);
        const uploadData = new FormData();
        uploadData.append('file', file);

        try {
            const config = {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${user.token}`
                }
            };
            const res = await axios.post(`${API_URL}/files/upload`, uploadData, config);
            setFormData(prev => ({ ...prev, logoUrl: res.data.url }));
            setMessage({ type: 'success', text: 'Logo/Wappen erfolgreich hochgeladen!' });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.message || 'Fehler beim Hochladen des Bildes' });
        } finally {
            setIsUploading(false);
        }
    };

    const handleGalleryUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        setIsGalleryUploading(true);
        try {
            const config = {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${user.token}`
                }
            };

            const uploadedUrls = [];
            for (const file of files) {
                const uploadData = new FormData();
                uploadData.append('file', file);
                const res = await axios.post(`${API_URL}/files/upload`, uploadData, config);
                if (res.data?.url) {
                    uploadedUrls.push(res.data.url);
                }
            }

            setFormData(prev => ({
                ...prev,
                images: [...(prev.images || []), ...uploadedUrls]
            }));
            setMessage({ type: 'success', text: `${uploadedUrls.length} Galerie-Foto(s) hochgeladen!` });
        } catch (err) {
            setMessage({ type: 'error', text: 'Fehler beim Hochladen der Galerie-Bilder' });
        } finally {
            setIsGalleryUploading(false);
            if (galleryInputRef.current) galleryInputRef.current.value = '';
        }
    };

    const handleAddGalleryUrl = () => {
        if (!galleryUrlInput.trim()) return;
        setFormData(prev => ({
            ...prev,
            images: [...(prev.images || []), galleryUrlInput.trim()]
        }));
        setGalleryUrlInput('');
    };

    const handleRemoveGalleryImage = (indexToRemove) => {
        setFormData(prev => ({
            ...prev,
            images: (prev.images || []).filter((_, idx) => idx !== indexToRemove)
        }));
    };

    const handleAddHighlight = () => {
        if (!highlightInput.trim()) return;
        const tag = highlightInput.trim();
        if (!formData.highlights?.includes(tag)) {
            setFormData(prev => ({
                ...prev,
                highlights: [...(prev.highlights || []), tag]
            }));
        }
        setHighlightInput('');
    };

    const handleRemoveHighlight = (tagToRemove) => {
        setFormData(prev => ({
            ...prev,
            highlights: (prev.highlights || []).filter(t => t !== tagToRemove)
        }));
    };

    const generateSlugFromName = () => {
        if (!formData.name) return;
        const slug = formData.name
            .toLowerCase()
            .replace(/ä/g, 'ae')
            .replace(/ö/g, 'oe')
            .replace(/ü/g, 'ue')
            .replace(/ß/g, 'ss')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
        setFormData(prev => ({ ...prev, slug }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            const payload = {
                ...formData,
                standardSchedule: formData.hasSchedule ? formData.standardSchedule : null,
                imageUrl: formData.logoUrl // ensure backwards compatibility
            };

            if (selectedGroup) {
                await axios.put(`${API_URL}/groups/${selectedGroup._id}`, payload, config);
                setMessage({ type: 'success', text: 'Gruppe aktualisiert!' });
            } else {
                await axios.post(`${API_URL}/groups`, payload, config);
                setMessage({ type: 'success', text: 'Gruppe erstellt!' });
            }

            setShowModal(false);
            resetForm();
            fetchGroups();
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.message || 'Fehler beim Speichern' });
        }
    };

    const handleDelete = async (groupId) => {
        if (!window.confirm('Möchten Sie diese Gruppe wirklich löschen?')) return;

        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            await axios.delete(`${API_URL}/groups/${groupId}`, config);
            setMessage({ type: 'success', text: 'Gruppe gelöscht!' });
            fetchGroups();
        } catch (error) {
            setMessage({ type: 'error', text: 'Fehler beim Löschen' });
        }
    };

    const handleToggleArchive = async (group, e) => {
        if (e) e.stopPropagation();
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            const newStatus = !group.isArchived;
            await axios.put(`${API_URL}/groups/${group._id}`, { isArchived: newStatus }, config);
            setMessage({
                type: 'success',
                text: newStatus ? `Gruppe "${group.name}" archiviert.` : `Gruppe "${group.name}" reaktiviert.`
            });
            fetchGroups();
        } catch (err) {
            setMessage({ type: 'error', text: 'Fehler beim Ändern des Archivierungsstatus.' });
        }
    };

    const openEditModal = (group) => {
        setSelectedGroup(group);
        const rawLeaders = group.leaders?.map(l => l._id || l) || [];
        const primaryContactId = group.primaryContactLeader || (group.primaryContact?._id) || (rawLeaders[0] || '');
        const hasSched = Boolean(
            group.standardSchedule && (
                group.standardSchedule.startTime ||
                group.standardSchedule.location ||
                (group.standardSchedule.dayOfWeek !== undefined && group.standardSchedule.dayOfWeek !== null && group.standardSchedule.dayOfWeek !== -1)
            )
        );

        let parsedImages = [];
        if (Array.isArray(group.images)) {
            parsedImages = group.images;
        } else if (typeof group.images === 'string' && group.images.startsWith('[')) {
            try { parsedImages = JSON.parse(group.images); } catch (e) { parsedImages = []; }
        }

        let parsedHighlights = [];
        if (Array.isArray(group.highlights)) {
            parsedHighlights = group.highlights;
        } else if (typeof group.highlights === 'string') {
            try {
                parsedHighlights = JSON.parse(group.highlights);
            } catch (e) {
                parsedHighlights = group.highlights.split(',').map(s => s.trim()).filter(Boolean);
            }
        }

        setFormData({
            name: group.name,
            slug: group.slug || '',
            altersstufe: group.altersstufe || 'Meute',
            birthYearStart: group.birthYearStart !== null && group.birthYearStart !== undefined ? group.birthYearStart : '',
            birthYearEnd: group.birthYearEnd !== null && group.birthYearEnd !== undefined ? group.birthYearEnd : '',
            primaryContactLeader: primaryContactId ? primaryContactId.toString() : '',
            motto: group.motto || '',
            logoUrl: group.logoUrl || group.imageUrl || '',
            description: group.description || '',
            longDescription: group.longDescription || '',
            images: parsedImages,
            highlights: parsedHighlights,
            color: group.color || '#009640',
            leaders: rawLeaders.map(id => id ? id.toString() : id),
            hasSchedule: hasSched,
            standardSchedule: group.standardSchedule || {
                dayOfWeek: 5,
                startTime: '',
                endTime: '',
                location: ''
            },
            contactEmail: group.contactEmail || '',
            order: group.order || 0,
            isActive: group.isActive !== false,
            isArchived: Boolean(group.isArchived),
            glowInTheDark: Boolean(group.glowInTheDark)
        });
        setShowModal(true);
    };

    const openMembersModal = (group) => {
        setSelectedGroup(group);
        setShowMembersModal(true);
    };

    const resetForm = () => {
        setSelectedGroup(null);
        setHighlightInput('');
        setGalleryUrlInput('');
        setFormData({
            name: '',
            slug: '',
            altersstufe: 'Meute',
            birthYearStart: '',
            birthYearEnd: '',
            primaryContactLeader: '',
            motto: '',
            logoUrl: '',
            description: '',
            longDescription: '',
            images: [],
            highlights: [],
            color: '#f57c00',
            leaders: [],
            hasSchedule: false,
            standardSchedule: { dayOfWeek: 5, startTime: '', endTime: '', location: '' },
            contactEmail: '',
            order: 0,
            isActive: true,
            isArchived: false,
            glowInTheDark: false
        });
    };

    const applyPreset = (preset) => {
        setFormData(prev => ({
            ...prev,
            altersstufe: preset.name,
            color: preset.color
        }));
    };

    const toggleLeader = (userId) => {
        setFormData(prev => {
            const uIdStr = userId.toString();
            const isCurrentlySelected = prev.leaders.some(id => id.toString() === uIdStr);
            const newLeaders = isCurrentlySelected
                ? prev.leaders.filter(id => id.toString() !== uIdStr)
                : [...prev.leaders, uIdStr];

            let newPrimary = prev.primaryContactLeader;
            if (isCurrentlySelected && prev.primaryContactLeader === uIdStr) {
                newPrimary = newLeaders.length > 0 ? newLeaders[0] : '';
            } else if (!isCurrentlySelected && newLeaders.length === 1) {
                newPrimary = uIdStr;
            }

            return {
                ...prev,
                leaders: newLeaders,
                primaryContactLeader: newPrimary
            };
        });
    };

    const setPrimaryContact = (userId) => {
        const uIdStr = userId.toString();
        setFormData(prev => ({
            ...prev,
            primaryContactLeader: uIdStr,
            leaders: prev.leaders.some(id => id.toString() === uIdStr) ? prev.leaders : [...prev.leaders, uIdStr]
        }));
    };

    const activeCount = groups.filter(g => !g.isArchived).length;
    const archivedCount = groups.filter(g => g.isArchived).length;

    const filteredGroups = groups.filter(group => {
        const matchesSearch = !searchTerm || 
            group.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            group.altersstufe?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            group.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            group.motto?.toLowerCase().includes(searchTerm.toLowerCase());
        
        if (archiveFilter === 'active') return matchesSearch && !group.isArchived;
        if (archiveFilter === 'archived') return matchesSearch && group.isArchived;
        return matchesSearch;
    });

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                <span className="ml-4 text-surface-onVariant">Lädt Gruppen...</span>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-4 max-w-7xl animate-fade-in">
            {/* Header */}
            <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-primary-container text-primary-onContainer flex items-center justify-center shadow-sm">
                        <Groups style={{ fontSize: 28 }} />
                    </div>
                    <div>
                        <h1 className="text-display-small font-bold text-surface-on">Gruppen & Stufen</h1>
                        <p className="text-body-medium text-surface-onVariant">{groups.length} Gruppen ({activeCount} aktiv, {archivedCount} archiviert)</p>
                    </div>
                </div>

                <button
                    data-tour="group-create-btn"
                    onClick={() => { resetForm(); setShowModal(true); }}
                    className="md3-btn-filled flex items-center gap-2 shadow-sm"
                >
                    <Add style={{ fontSize: 20 }} />
                    Neue Gruppe anlegen
                </button>
            </div>

            {/* Search & Filter Bar */}
            <div className="mb-6 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
                <div className="flex items-center gap-2 overflow-x-auto pb-1">
                    <button
                        onClick={() => setArchiveFilter('active')}
                        className={`px-4 py-2 rounded-2xl text-xs font-bold transition-all shrink-0 ${
                            archiveFilter === 'active'
                                ? 'bg-primary text-primary-on shadow-sm'
                                : 'bg-surface-containerHigh text-surface-on hover:bg-surface-containerHighest'
                        }`}
                    >
                        Aktive Gruppen ({activeCount})
                    </button>
                    <button
                        onClick={() => setArchiveFilter('archived')}
                        className={`px-4 py-2 rounded-2xl text-xs font-bold transition-all shrink-0 ${
                            archiveFilter === 'archived'
                                ? 'bg-amber-600 text-white shadow-sm'
                                : 'bg-surface-containerHigh text-surface-on hover:bg-surface-containerHighest'
                        }`}
                    >
                        Archiviert ({archivedCount})
                    </button>
                    <button
                        onClick={() => setArchiveFilter('all')}
                        className={`px-4 py-2 rounded-2xl text-xs font-bold transition-all shrink-0 ${
                            archiveFilter === 'all'
                                ? 'bg-surface-on text-surface shadow-sm'
                                : 'bg-surface-containerHigh text-surface-on hover:bg-surface-containerHighest'
                        }`}
                    >
                        Alle ({groups.length})
                    </button>
                </div>

                <div className="relative md:w-72">
                    <Search style={{ fontSize: 18 }} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-onVariant" />
                    <input
                        type="text"
                        placeholder="Gruppe suchen..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="md3-textfield-outlined pl-10 text-xs w-full py-2"
                    />
                </div>
            </div>

            {/* Alert */}
            {message.text && (
                <div className={`mb-6 p-4 rounded-2xl flex items-center gap-3 animate-fade-in ${message.type === 'error' ? 'bg-error-container text-error-on-container' : 'bg-primary-container text-primary-onContainer'
                    }`}>
                    {message.type === 'error' ? '⚠️' : '✅'}
                    <span className="font-medium">{message.text}</span>
                    <button onClick={() => setMessage({ type: '', text: '' })} className="ml-auto p-1 hover:opacity-80">
                        <X style={{ fontSize: 18 }} />
                    </button>
                </div>
            )}

            {/* Groups Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredGroups.map(group => (
                    <div
                        key={group._id}
                        className={`md3-card-elevated p-6 hover:shadow-lg transition-all rounded-3xl flex flex-col justify-between border ${
                            group.isArchived
                                ? 'bg-surface-containerLow/60 border-amber-500/40 opacity-80'
                                : 'border-outlineVariant/30'
                        }`}
                    >
                        <div>
                            {/* Header with Logo/Wappen + Title */}
                            <div className="flex items-start gap-4 mb-4">
                                {group.logoUrl || group.imageUrl ? (
                                    <div className="relative shrink-0">
                                        <img
                                            src={group.logoUrl || group.imageUrl}
                                            alt={group.name}
                                            className={`w-16 h-16 rounded-full object-cover shadow-sm border bg-surface-container ${
                                                group.glowInTheDark
                                                    ? 'border-[#14FF00] shadow-[0_0_12px_rgba(20,255,0,0.35)]'
                                                    : 'border-outlineVariant/30'
                                            }`}
                                        />
                                        {group.glowInTheDark && (
                                            <span
                                                className="absolute -bottom-1 -right-1 bg-black text-[#14FF00] text-[10px] px-1.5 py-0.5 rounded-full font-bold border border-[#14FF00] shadow-sm flex items-center gap-0.5"
                                                title="Nachtleuchtendes Abzeichen (Glow in the Dark) aktiviert"
                                            >
                                                ✨ Glow
                                            </span>
                                        )}
                                    </div>
                                ) : (
                                    <div
                                        className={`w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-2xl shadow-sm shrink-0 relative ${
                                            group.glowInTheDark
                                                ? 'border-2 border-[#14FF00] shadow-[0_0_12px_rgba(20,255,0,0.35)]'
                                                : ''
                                        }`}
                                        style={{ backgroundColor: group.color || '#009640' }}
                                    >
                                        {group.name.charAt(0)}
                                        {group.glowInTheDark && (
                                            <span
                                                className="absolute -bottom-1 -right-1 bg-black text-[#14FF00] text-[10px] px-1.5 py-0.5 rounded-full font-bold border border-[#14FF00] shadow-sm flex items-center gap-0.5"
                                                title="Nachtleuchtendes Abzeichen aktiviert"
                                            >
                                                ✨
                                            </span>
                                        )}
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h3 className="text-title-large font-bold text-surface-on truncate">{group.name}</h3>
                                        <span
                                            className="px-2.5 py-0.5 rounded-full text-xs font-semibold"
                                            style={{
                                                backgroundColor: `${group.color || '#009640'}20`,
                                                color: group.color || '#009640'
                                            }}
                                        >
                                            {group.altersstufe || 'Gruppe'}
                                        </span>
                                        {getGroupAgeInfo(group) && (
                                            <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-surface-containerHigh text-surface-onVariant">
                                                {getGroupAgeInfo(group).badge}
                                            </span>
                                        )}
                                        {group.isArchived && (
                                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-500/20 text-amber-800 dark:text-amber-300 border border-amber-500/30">
                                                Archiviert
                                            </span>
                                        )}
                                    </div>
                                    {group.motto && (
                                        <p className="text-body-small italic text-primary font-medium mt-1">
                                            „{group.motto}“
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Description */}
                            {group.description && (
                                <p className="text-body-medium text-surface-onVariant mb-4 line-clamp-3">
                                    {group.description}
                                </p>
                            )}

                            {/* Schedule Box (Only if defined) */}
                            {Boolean(
                                (group.standardSchedule && (
                                    group.standardSchedule.startTime ||
                                    group.standardSchedule.location ||
                                    (group.standardSchedule.dayOfWeek !== undefined && group.standardSchedule.dayOfWeek !== null && group.standardSchedule.dayOfWeek !== -1)
                                )) || group.meetingTime
                            ) && (
                                <div className="p-3 bg-surface-containerLow rounded-2xl mb-4 border border-outlineVariant/20">
                                    <div className="flex items-center gap-2 text-body-small text-surface-on font-medium">
                                        <Calendar style={{ fontSize: 16 }} className="text-primary" />
                                        <span>
                                            {group.standardSchedule?.dayOfWeek !== undefined && group.standardSchedule?.dayOfWeek !== null && group.standardSchedule?.dayOfWeek !== -1
                                                ? weekDays[group.standardSchedule.dayOfWeek]
                                                : (group.meetingTime || 'Regelmäßig')}
                                        </span>
                                        {group.standardSchedule?.startTime && (
                                            <>
                                                <Clock style={{ fontSize: 16 }} className="ml-2 text-primary" />
                                                <span>{group.standardSchedule.startTime}{group.standardSchedule.endTime ? ` - ${group.standardSchedule.endTime}` : ''} Uhr</span>
                                            </>
                                        )}
                                    </div>
                                    {group.standardSchedule?.location && (
                                        <div className="flex items-center gap-2 text-body-small text-surface-onVariant mt-1">
                                            <Location style={{ fontSize: 16 }} className="text-primary" />
                                            <span>{group.standardSchedule.location}</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Ansprechpersonen / Leaders */}
                            {group.leaders && group.leaders.length > 0 && (
                                <div className="mb-4">
                                    <span className="text-xs font-semibold text-surface-onVariant block mb-1.5 uppercase tracking-wider">
                                        Leitung & Ansprechperson
                                    </span>
                                    <div className="flex flex-wrap gap-2">
                                        {group.leaders.map((leader, idx) => {
                                            const lData = typeof leader === 'object' ? (leader.userData || leader) : {};
                                            const name = lData.scoutName || lData.displayName || lData.firstName || leader.username || 'Leiter';
                                            const photo = (typeof lData.profilePhoto === 'string' && lData.profilePhoto)
                                                ? lData.profilePhoto
                                                : (lData.profilePhoto?.url || (Array.isArray(lData.photos) && lData.photos[0]?.url) || lData.avatarConfig3D?.modelUrl || lData.avatarConfig?.modelUrl || leader.avatarUrl || lData.avatarUrl || null);
                                            const isPrimary = leader.isPrimaryContact || (group.primaryContactLeader && (leader._id === group.primaryContactLeader || leader === group.primaryContactLeader)) || idx === 0;
                                            return (
                                                <div
                                                    key={leader._id || idx}
                                                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
                                                        isPrimary
                                                            ? 'bg-amber-500/15 border-amber-500/40 text-amber-900 dark:text-amber-200'
                                                            : 'bg-surface-container text-surface-on border-outlineVariant/30'
                                                    }`}
                                                >
                                                    {photo ? (
                                                        <img src={photo} alt={name} className="w-5 h-5 rounded-full object-cover" />
                                                    ) : (
                                                        <div className="w-5 h-5 rounded-full bg-primary-container text-primary-onContainer text-[10px] font-bold flex items-center justify-center">
                                                            {name.charAt(0).toUpperCase()}
                                                        </div>
                                                    )}
                                                    <span>{name}</span>
                                                    {isPrimary && (
                                                        <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400">Kontakt</span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Contact Email */}
                            {group.contactEmail && (
                                <div className="flex items-center gap-2 text-xs text-surface-onVariant mb-4">
                                    <Email style={{ fontSize: 14 }} className="text-primary" />
                                    <span>{group.contactEmail}</span>
                                </div>
                            )}
                        </div>

                        {/* Card Footer Actions */}
                        <div className="flex items-center gap-2 pt-4 border-t border-outlineVariant/40">
                            <a
                                href={`${getMainUrl()}/gruppen/${group.slug || group._id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2.5 rounded-xl hover:bg-primary-container text-primary transition-colors flex items-center justify-center"
                                title="Öffentliche Detailseite im neuen Tab öffnen"
                            >
                                <Globe style={{ fontSize: 20 }} />
                            </a>
                            <button
                                onClick={() => openMembersModal(group)}
                                className="md3-btn-tonal flex-1 text-sm py-2 rounded-xl"
                            >
                                <Person style={{ fontSize: 18 }} className="mr-1" />
                                {group.members?.length || 0} Mitglieder
                            </button>
                            <button
                                onClick={(e) => handleToggleArchive(group, e)}
                                className={`px-3 py-2 rounded-xl text-xs font-semibold transition-colors ${
                                    group.isArchived
                                        ? 'bg-amber-500/20 text-amber-800 dark:text-amber-300 hover:bg-amber-500/30'
                                        : 'hover:bg-surface-containerHighest text-surface-onVariant'
                                }`}
                                title={group.isArchived ? 'Gruppe wiederherstellen (reaktivieren)' : 'Gruppe archivieren (auf Startseite & Übersicht ausblenden)'}
                            >
                                {group.isArchived ? 'Reaktivieren' : 'Archivieren'}
                            </button>
                            <button
                                onClick={() => openEditModal(group)}
                                className="p-2.5 rounded-xl hover:bg-primary-container text-primary transition-colors"
                                title="Bearbeiten"
                            >
                                <Edit style={{ fontSize: 20 }} />
                            </button>
                            <button
                                onClick={() => handleDelete(group._id)}
                                className="p-2.5 rounded-xl hover:bg-error-container text-error transition-colors"
                                title="Löschen"
                            >
                                <Delete style={{ fontSize: 20 }} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {filteredGroups.length === 0 && (
                <div className="text-center py-16 text-surface-onVariant bg-surface-containerLow rounded-3xl border border-outlineVariant/30">
                    <Groups style={{ fontSize: 64 }} className="mx-auto mb-4 opacity-30 text-primary" />
                    <p className="text-title-large font-bold text-surface-on">Keine passenden Gruppen gefunden</p>
                    <p className="text-body-medium mt-1">
                        {archiveFilter === 'archived'
                            ? 'Es sind aktuell keine Gruppen archiviert.'
                            : 'Erstelle eine neue Gruppe oder passe deinen Suchfilter an.'}
                    </p>
                </div>
            )}

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-scrim/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div className="md3-card-elevated max-w-2xl w-full p-6 sm:p-8 animate-scale-in max-h-[90vh] overflow-y-auto rounded-3xl relative">
                        <MicroTour 
                            tourId="create-group" 
                            isOpen={showMicroTour} 
                            onClose={() => setShowMicroTour(false)} 
                        />

                        <div className="flex items-center justify-between mb-6 pb-3 border-b border-outlineVariant/40">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-2xl bg-primary-container text-primary-onContainer flex items-center justify-center">
                                    <Groups style={{ fontSize: 20 }} />
                                </div>
                                <div>
                                    <h2 className="text-title-large font-bold text-surface-on">
                                        {selectedGroup ? 'Gruppe bearbeiten' : 'Neue Gruppe anlegen'}
                                    </h2>
                                    <p className="text-body-small text-surface-onVariant">Angaben zur Präsentation auf der Website</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => setShowMicroTour(true)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-primary/10 hover:bg-primary/20 text-primary text-xs font-semibold transition-colors cursor-pointer"
                                    title="Erklärung zum Gruppen-Formular anzeigen"
                                >
                                    <HelpCircle className="w-3.5 h-3.5" />
                                    <span>Hilfe / Tour</span>
                                </button>
                                <button onClick={() => { setShowModal(false); resetForm(); }} className="p-2 hover:bg-surface-container rounded-full cursor-pointer">
                                    <X style={{ fontSize: 20 }} />
                                </button>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Stufen Preset Schnellwahl & Basic Info */}
                            <div data-microtour="group-basics" className="p-3.5 rounded-2xl bg-surface-containerLowest/50 border border-outlineVariant/30 space-y-4">
                                <div>
                                    <label className="block text-label-large text-surface-onVariant mb-2 font-medium">Stufen-Vorlagen</label>
                                    <div className="grid grid-cols-3 gap-2">
                                        {STUFEN_PRESETS.map(preset => (
                                            <button
                                                key={preset.name}
                                                type="button"
                                                onClick={() => applyPreset(preset)}
                                                className={`py-2 px-3 rounded-xl border text-xs font-semibold flex items-center justify-center gap-2 transition-all cursor-pointer ${
                                                    formData.altersstufe === preset.name
                                                        ? 'border-primary bg-primary-container/40 text-primary shadow-sm'
                                                        : 'border-outlineVariant/40 hover:bg-surface-containerLow text-surface-onVariant'
                                                }`}
                                            >
                                                <span
                                                    className="w-2.5 h-2.5 rounded-full"
                                                    style={{ backgroundColor: preset.color }}
                                                />
                                                <span className="truncate">{preset.name}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-label-large text-surface-onVariant mb-2 font-medium">Gruppenname *</label>
                                        <input
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            className="md3-textfield-outlined"
                                            placeholder="z.B. Meute Graue Wölfe"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-label-large text-surface-onVariant mb-2 font-medium">Kategorie / Stufe *</label>
                                        <select
                                            value={formData.altersstufe}
                                            onChange={(e) => setFormData({ ...formData, altersstufe: e.target.value })}
                                            className="md3-textfield-outlined"
                                        >
                                            {altersstufen.map(a => (
                                                <option key={a} value={a}>{a}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Slug / Web-Adresse */}
                            <div className="p-4 rounded-2xl bg-surface-containerLow border border-outlineVariant/30 space-y-2">
                                <div className="flex items-center justify-between">
                                    <label className="block text-label-medium font-bold text-surface-on">
                                        URL-Slug (Webseiten-Adresse)
                                    </label>
                                    <button
                                        type="button"
                                        onClick={generateSlugFromName}
                                        className="text-xs text-primary hover:underline font-semibold"
                                    >
                                        Aus Name generieren
                                    </button>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-surface-onVariant font-mono bg-surface-container px-3 py-2.5 rounded-xl border border-outlineVariant/40">
                                        /gruppen/
                                    </span>
                                    <input
                                        type="text"
                                        value={formData.slug}
                                        onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                                        className="md3-textfield-outlined font-mono text-sm flex-1"
                                        placeholder="z.B. meute-graue-woelfe"
                                    />
                                </div>
                                <p className="text-[11px] text-surface-onVariant">
                                    Unter dieser URL ist die Detailseite öffentlich erreichbar (z.B. <code>/gruppen/{formData.slug || 'slug'}</code>).
                                </p>
                            </div>

                            {/* Dynamic Age & Birth Year Info */}
                            {formData.altersstufe === 'Meute' ? (
                                <div className="p-3.5 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-surface-on text-xs space-y-1 animate-fade-in">
                                    <div className="font-bold flex items-center gap-1.5 text-amber-700 dark:text-amber-300 text-sm">
                                        <span>🐾</span>
                                        <span>Meute: Feste Altersspanne 6 bis 10 Jahre</span>
                                    </div>
                                    <p className="text-surface-onVariant">
                                        Bei Meuten ist das Alter immer fest auf 6–10 Jahre gesetzt. Auf der Website werden die Geburtsjahrgänge öffentlich dynamisch über das aktuelle Datum ermittelt (heute: <strong>Jahrgänge {new Date().getFullYear() - 10}–{new Date().getFullYear() - 6}</strong>, in zwei Jahren entsprechend angepasst).
                                    </p>
                                </div>
                            ) : (
                                <div className="p-4 rounded-2xl bg-surface-containerLow border border-outlineVariant/30 space-y-3 animate-fade-in">
                                    <div>
                                        <label className="block text-label-medium font-bold text-surface-on mb-1">
                                            Jahrgangsbereich der Kinder (optional)
                                        </label>
                                        <p className="text-body-small text-surface-onVariant">
                                            Gib den Geburtsjahrgangsbereich an. Das Alter der Kinder wird automatisch auf Basis des aktuellen Jahres berechnet.
                                        </p>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-label-small text-surface-onVariant mb-1">Von Jahrgang (z.B. 2012)</label>
                                            <input
                                                type="number"
                                                value={formData.birthYearStart || ''}
                                                onChange={(e) => setFormData({ ...formData, birthYearStart: e.target.value })}
                                                className="md3-textfield-outlined text-sm"
                                                placeholder="z.B. 2012"
                                                min="1990"
                                                max="2035"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-label-small text-surface-onVariant mb-1">Bis Jahrgang (z.B. 2015)</label>
                                            <input
                                                type="number"
                                                value={formData.birthYearEnd || ''}
                                                onChange={(e) => setFormData({ ...formData, birthYearEnd: e.target.value })}
                                                className="md3-textfield-outlined text-sm"
                                                placeholder="z.B. 2015"
                                                min="1990"
                                                max="2035"
                                            />
                                        </div>
                                    </div>
                                    {getGroupAgeInfo(formData) && (
                                        <div className="text-xs font-semibold text-primary pt-1 flex items-center gap-1.5">
                                            <span>ℹ️</span>
                                            <span>Vorschau für Website: {getGroupAgeInfo(formData).badge}</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Motto */}
                            <div>
                                <label className="block text-label-large text-surface-onVariant mb-2 font-medium">
                                    Motto / Leitspruch
                                </label>
                                <input
                                    type="text"
                                    value={formData.motto}
                                    onChange={(e) => setFormData({ ...formData, motto: e.target.value })}
                                    className="md3-textfield-outlined"
                                    placeholder="z.B. Unser Bestes! oder Allzeit Bereit!"
                                />
                            </div>

                            {/* Logo / Wappen Upload */}
                            <div>
                                <label className="block text-label-large text-surface-onVariant mb-2 font-medium">
                                    Logo / Wappen / Gruppenabzeichen (Rund)
                                </label>
                                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                                    {formData.logoUrl ? (
                                        <div className={`relative w-20 h-20 rounded-full overflow-hidden border shadow-sm bg-surface-container shrink-0 ${
                                            formData.glowInTheDark
                                                ? 'border-[#14FF00] shadow-[0_0_15px_rgba(20,255,0,0.4)]'
                                                : 'border-outlineVariant'
                                        }`}>
                                            <img src={formData.logoUrl} alt="Logo Vorschau" className="w-full h-full object-cover" />
                                            <button
                                                type="button"
                                                onClick={() => setFormData({ ...formData, logoUrl: '' })}
                                                className="absolute top-1 right-1 p-1 bg-black/60 text-white rounded-full hover:bg-black"
                                            >
                                                <X style={{ fontSize: 12 }} />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="w-20 h-20 rounded-full border-2 border-dashed border-outlineVariant flex flex-col items-center justify-center text-surface-onVariant shrink-0">
                                            <Image style={{ fontSize: 24 }} />
                                            <span className="text-[10px] mt-1">Rund</span>
                                        </div>
                                    )}
                                    <div className="flex-1 w-full space-y-2">
                                        <input
                                            type="text"
                                            value={formData.logoUrl}
                                            onChange={(e) => setFormData({ ...formData, logoUrl: e.target.value })}
                                            className="md3-textfield-outlined text-sm"
                                            placeholder="Bild-URL oder Datei hochladen"
                                        />
                                        <div className="flex gap-2">
                                            <input
                                                type="file"
                                                ref={fileInputRef}
                                                onChange={handleLogoUpload}
                                                accept="image/*"
                                                className="hidden"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => fileInputRef.current?.click()}
                                                disabled={isUploading}
                                                className="md3-btn-tonal text-xs py-1.5 px-3 flex items-center gap-1.5"
                                            >
                                                <Upload style={{ fontSize: 16 }} />
                                                {isUploading ? 'Lädt hoch...' : 'Datei auswählen'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Easter Egg: Nachtleuchtendes Abzeichen (Glow in the Dark) */}
                            <div className="p-4 bg-surface-containerLow rounded-2xl border border-outlineVariant/30 transition-all hover:border-[#14FF00]/50">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="text-base">✨</span>
                                            <label htmlFor="glowInTheDarkToggle" className="text-title-small font-bold text-surface-on cursor-pointer flex items-center gap-2">
                                                Nachtleuchtendes Abzeichen (Glow in the Dark)
                                                {formData.glowInTheDark && (
                                                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-black text-[#14FF00] border border-[#14FF00] font-mono">
                                                        #14FF00
                                                    </span>
                                                )}
                                            </label>
                                        </div>
                                        <p className="text-body-small text-surface-onVariant mt-1">
                                            Easter Egg: Manche Gruppen haben im Dunkeln leuchtende Abzeichen. Wenn aktiviert, werden alle weißen Bereiche im Logo im Dark Mode in leuchtendem Neongrün (<span className="font-mono font-bold text-[#14FF00] bg-black px-1 rounded">#14FF00</span>) dargestellt.
                                        </p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                                        <input
                                            id="glowInTheDarkToggle"
                                            type="checkbox"
                                            checked={formData.glowInTheDark || false}
                                            onChange={(e) => setFormData({ ...formData, glowInTheDark: e.target.checked })}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-surface-variant peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#009640]"></div>
                                    </label>
                                </div>
                            </div>

                            {/* Short Description */}
                            <div>
                                <label className="block text-label-large text-surface-onVariant mb-2 font-medium">Kurzbeschreibung (für die Übersichtskarte)</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="md3-textfield-outlined min-h-[80px]"
                                    placeholder="Kompakte Vorstellung der Gruppe auf der Hauptseite..."
                                />
                            </div>

                            {/* Long Description (Detail Page) */}
                            <div>
                                <label className="block text-label-large text-surface-onVariant mb-2 font-medium">
                                    Ausführliche Vorstellung & „Über uns“ (Detailseite)
                                </label>
                                <textarea
                                    value={formData.longDescription}
                                    onChange={(e) => setFormData({ ...formData, longDescription: e.target.value })}
                                    className="md3-textfield-outlined min-h-[140px]"
                                    placeholder="Ausführliche Beschreibung für die Gruppen-Detailseite: Was machen wir in den Gruppenstunden? Was macht unsere Stufe besonders? Wie sieht ein typisches Lager aus?"
                                />
                            </div>

                            {/* Highlights / Schlagwörter */}
                            <div>
                                <label className="block text-label-large text-surface-onVariant mb-2 font-medium">
                                    Highlights & Schlagwörter (für die Detailseite)
                                </label>
                                <div className="flex gap-2 mb-2">
                                    <input
                                        type="text"
                                        value={highlightInput}
                                        onChange={(e) => setHighlightInput(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleAddHighlight();
                                            }
                                        }}
                                        className="md3-textfield-outlined text-sm flex-1"
                                        placeholder="z.B. Zeltlager, Natur entdecken, Gemeinschaft, Feuer machen..."
                                    />
                                    <button
                                        type="button"
                                        onClick={handleAddHighlight}
                                        className="md3-btn-tonal text-xs px-4 shrink-0 font-bold"
                                    >
                                        + Hinzufügen
                                    </button>
                                </div>
                                {formData.highlights && formData.highlights.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 pt-1">
                                        {formData.highlights.map((tag, idx) => (
                                            <span
                                                key={idx}
                                                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-primary-container text-primary-onContainer"
                                            >
                                                <span>✓ {tag}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveHighlight(tag)}
                                                    className="hover:text-error transition-colors"
                                                >
                                                    <X style={{ fontSize: 14 }} />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Photo Gallery (Detail Page) */}
                            <div className="p-4 bg-surface-containerLow rounded-2xl border border-outlineVariant/30 space-y-4">
                                <div>
                                    <h3 className="text-title-small font-bold text-surface-on flex items-center gap-2">
                                        <Image style={{ fontSize: 18 }} className="text-primary" />
                                        <span>Fotogalerie der Gruppe (Detailseite)</span>
                                    </h3>
                                    <p className="text-body-small text-surface-onVariant mt-0.5">
                                        Lade schöne Impressionen von Gruppenstunden und Lagern hoch. Auf der Detailseite erscheint eine interaktive Lightbox-Galerie.
                                    </p>
                                </div>

                                {/* Upload & URL inputs */}
                                <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                                    <input
                                        type="file"
                                        ref={galleryInputRef}
                                        onChange={handleGalleryUpload}
                                        accept="image/*"
                                        multiple
                                        className="hidden"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => galleryInputRef.current?.click()}
                                        disabled={isGalleryUploading}
                                        className="md3-btn-filled text-xs py-2 px-3 flex items-center justify-center gap-1.5 shrink-0"
                                    >
                                        <Upload style={{ fontSize: 16 }} />
                                        <span>{isGalleryUploading ? 'Lädt hoch...' : 'Fotos hochladen (Multi)'}</span>
                                    </button>

                                    <div className="flex flex-1 gap-2">
                                        <input
                                            type="url"
                                            value={galleryUrlInput}
                                            onChange={(e) => setGalleryUrlInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleAddGalleryUrl();
                                                }
                                            }}
                                            className="md3-textfield-outlined text-xs flex-1"
                                            placeholder="Oder Bild-URL einfügen..."
                                        />
                                        <button
                                            type="button"
                                            onClick={handleAddGalleryUrl}
                                            className="md3-btn-tonal text-xs px-3 font-bold"
                                        >
                                            +
                                        </button>
                                    </div>
                                </div>

                                {/* Gallery Thumbnails Grid */}
                                {formData.images && formData.images.length > 0 ? (
                                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 pt-2">
                                        {formData.images.map((imgUrl, idx) => (
                                            <div
                                                key={idx}
                                                className="relative group rounded-xl overflow-hidden aspect-square border border-outlineVariant/40 shadow-xs bg-surface-container"
                                            >
                                                <img
                                                    src={imgUrl}
                                                    alt={`Galerie ${idx + 1}`}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveGalleryImage(idx)}
                                                    className="absolute top-1 right-1 p-1 rounded-full bg-black/70 text-white hover:bg-error transition-colors opacity-90 group-hover:opacity-100"
                                                    title="Foto entfernen"
                                                >
                                                    <X style={{ fontSize: 14 }} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-xs text-surface-onVariant/70 italic">
                                        Noch keine Galerie-Fotos hinzugefügt.
                                    </p>
                                )}
                            </div>

                            {/* Material Design 3 Group Color Generator */}
                            <GroupColorPicker
                                value={formData.color}
                                onChange={(col) => setFormData({ ...formData, color: col })}
                                groupName={formData.name}
                                altersstufe={formData.altersstufe}
                            />

                            <div>
                                <label className="block text-label-large text-surface-onVariant mb-2 font-medium">Kontakt-E-Mail (optional)</label>
                                <input
                                    type="email"
                                    value={formData.contactEmail}
                                    onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                                    className="md3-textfield-outlined"
                                    placeholder="woelflinge@pfadfinder-wutoeschingen.org"
                                />
                            </div>

                            {/* Standard Schedule (Optional) */}
                            <div data-microtour="group-schedule" className="p-4 bg-surface-containerLow rounded-2xl border border-outlineVariant/30 space-y-3">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="text-title-small font-bold text-surface-on">Regelmäßige Gruppenstunde (optional)</h3>
                                        <p className="text-body-small text-surface-onVariant">Keine Pflicht. Aktiviere dies nur, wenn feste Zeiten/Orte angezeigt werden sollen.</p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer shrink-0">
                                        <input
                                            type="checkbox"
                                            checked={formData.hasSchedule || false}
                                            onChange={(e) => setFormData({ ...formData, hasSchedule: e.target.checked })}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-surface-variant peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                                    </label>
                                </div>

                                {formData.hasSchedule && (
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-3 animate-fade-in border-t border-outlineVariant/20">
                                        <div>
                                            <label className="block text-label-medium text-surface-onVariant mb-1">Wochentag</label>
                                            <select
                                                value={formData.standardSchedule?.dayOfWeek ?? 5}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    standardSchedule: { ...(formData.standardSchedule || {}), dayOfWeek: parseInt(e.target.value) }
                                                })}
                                                className="md3-textfield-outlined text-sm"
                                            >
                                                {weekDays.map((day, idx) => (
                                                    <option key={day} value={idx}>{day}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <label className="block text-label-medium text-surface-onVariant mb-1">Von</label>
                                                <input
                                                    type="time"
                                                    value={formData.standardSchedule?.startTime || ''}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        standardSchedule: { ...(formData.standardSchedule || {}), startTime: e.target.value }
                                                    })}
                                                    className="md3-textfield-outlined text-sm"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-label-medium text-surface-onVariant mb-1">Bis</label>
                                                <input
                                                    type="time"
                                                    value={formData.standardSchedule?.endTime || ''}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        standardSchedule: { ...(formData.standardSchedule || {}), endTime: e.target.value }
                                                    })}
                                                    className="md3-textfield-outlined text-sm"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-label-medium text-surface-onVariant mb-1">Ort</label>
                                            <input
                                                type="text"
                                                value={formData.standardSchedule?.location || ''}
                                                onChange={(e) => setFormData({
                                                    ...formData,
                                                    standardSchedule: { ...(formData.standardSchedule || {}), location: e.target.value }
                                                })}
                                                className="md3-textfield-outlined text-sm"
                                                placeholder="z.B. Pfadfinderheim"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Leaders Selection */}
                            <div data-microtour="group-leaders">
                                <div className="flex items-center justify-between mb-1">
                                    <label className="block text-label-large text-surface-on font-medium">Gruppenleitung & Ansprechperson</label>
                                    <span className="text-xs text-surface-onVariant font-semibold">
                                        {formData.leaders.length} ausgewählt
                                    </span>
                                </div>
                                <p className="text-body-small text-surface-onVariant mb-3">
                                    Wähle mehrere Gruppenleiter aus und markiere genau eine <strong>Haupt-Ansprechperson (⭐)</strong> für Kontaktanfragen.
                                </p>
                                <div className="max-h-56 overflow-y-auto border border-outlineVariant/40 rounded-2xl p-2 space-y-1.5 bg-surface-containerLow">
                                    {users.filter(u => u.role === 'gruppenleiter' || u.role === 'admin' || u.userData?.isTeamMember).map(u => {
                                        const isSelected = formData.leaders.some(id => id.toString() === u._id.toString());
                                        const isPrimary = isSelected && (formData.primaryContactLeader?.toString() === u._id.toString() || (formData.leaders.length === 1 && !formData.primaryContactLeader));
                                        const name = u.userData?.scoutName ? `${u.userData.scoutName} (${u.userData?.firstName || ''} ${u.userData?.lastName || ''})`.trim() : (u.userData?.displayName || `${u.userData?.firstName || ''} ${u.userData?.lastName || ''}`.trim() || u.username || 'Unbekannt');
                                        return (
                                            <div
                                                key={u._id}
                                                className={`flex items-center gap-3 p-2.5 rounded-xl transition-all border ${
                                                    isSelected ? 'bg-surface-containerHigh border-primary/40' : 'hover:bg-surface-container border-transparent text-surface-on'
                                                }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    id={`leader-${u._id}`}
                                                    checked={isSelected}
                                                    onChange={() => toggleLeader(u._id)}
                                                    className="accent-primary w-4 h-4 rounded cursor-pointer shrink-0"
                                                />
                                                <label htmlFor={`leader-${u._id}`} className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer">
                                                    {(() => {
                                                        const photo = (typeof u.userData?.profilePhoto === 'string' && u.userData.profilePhoto)
                                                            ? u.userData.profilePhoto
                                                            : (u.userData?.profilePhoto?.url || (Array.isArray(u.userData?.photos) && u.userData.photos[0]?.url) || u.userData?.avatarConfig3D?.modelUrl || u.userData?.avatarConfig?.modelUrl || u.avatarUrl || null);
                                                        return photo ? (
                                                            <img src={photo} alt={name} className="w-8 h-8 rounded-full object-cover shrink-0" />
                                                        ) : (
                                                            <div className="w-8 h-8 rounded-full bg-surface-variant text-surface-onVariant text-xs font-bold flex items-center justify-center shrink-0">
                                                                {name.charAt(0).toUpperCase()}
                                                            </div>
                                                        );
                                                    })()}
                                                    <div className="flex-1 min-w-0">
                                                        <span className="text-body-medium font-medium truncate block">{name}</span>
                                                        <span className="text-xs opacity-75 truncate block">{u.officialEmail || u.email}</span>
                                                    </div>
                                                </label>
                                                {isSelected && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setPrimaryContact(u._id)}
                                                        className={`px-2.5 py-1 rounded-full text-xs font-bold transition-all flex items-center gap-1 shrink-0 ${
                                                            isPrimary
                                                                ? 'bg-amber-500 text-white shadow-sm ring-2 ring-amber-400'
                                                                : 'bg-surface-container text-surface-onVariant hover:bg-amber-500/20 hover:text-amber-700'
                                                        }`}
                                                        title={isPrimary ? 'Haupt-Ansprechperson' : 'Als Haupt-Ansprechperson festlegen'}
                                                    >
                                                        <span>{isPrimary ? '⭐ Ansprechperson' : 'Als Kontakt setzen'}</span>
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Archive & Status Section */}
                            <div className="p-4 bg-surface-containerLow rounded-2xl border border-outlineVariant/30 space-y-3">
                                <label className="flex items-center justify-between cursor-pointer">
                                    <div>
                                        <span className="text-body-medium font-bold text-surface-on block">Gruppe archivieren</span>
                                        <span className="text-body-small text-surface-onVariant block">
                                            Archivierte Gruppen werden auf der öffentlichen Website und Startseite ausgeblendet, bleiben aber im Archiv erhalten.
                                        </span>
                                    </div>
                                    <input
                                        type="checkbox"
                                        checked={formData.isArchived}
                                        onChange={(e) => setFormData(prev => ({ ...prev, isArchived: e.target.checked }))}
                                        className="w-5 h-5 accent-primary rounded cursor-pointer shrink-0 ml-4"
                                    />
                                </label>
                            </div>

                            {/* Buttons */}
                            <div data-microtour="group-submit" className="flex gap-3 pt-4 border-t border-outlineVariant/40">
                                <button type="submit" className="md3-btn-filled flex-1 py-2.5 rounded-xl font-bold">
                                    {selectedGroup ? 'Änderungen speichern' : 'Gruppe anlegen'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setShowModal(false); resetForm(); }}
                                    className="md3-btn-text flex-1 py-2.5 rounded-xl"
                                >
                                    Abbrechen
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Members Modal */}
            {showMembersModal && selectedGroup && (
                <div className="fixed inset-0 bg-scrim/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div className="md3-card-elevated max-w-2xl w-full p-6 sm:p-8 animate-scale-in max-h-[90vh] overflow-y-auto rounded-3xl">
                        <div className="flex items-center justify-between mb-6 pb-3 border-b border-outlineVariant/40">
                            <div className="flex items-center gap-3">
                                <div
                                    className="w-10 h-10 rounded-2xl flex items-center justify-center text-white font-bold shadow-sm"
                                    style={{ backgroundColor: selectedGroup.color || '#009640' }}
                                >
                                    {selectedGroup.name.charAt(0)}
                                </div>
                                <div>
                                    <h2 className="text-title-large font-bold text-surface-on">{selectedGroup.name}</h2>
                                    <p className="text-body-small text-surface-onVariant">Mitgliederverwaltung ({selectedGroup.members?.length || 0})</p>
                                </div>
                            </div>
                            <button onClick={() => setShowMembersModal(false)} className="p-2 hover:bg-surface-container rounded-full">
                                <X style={{ fontSize: 24 }} />
                            </button>
                        </div>

                        {/* Current Members */}
                        <div className="mb-6">
                            <h3 className="text-title-small font-bold mb-3 text-surface-on">Aktuelle Gruppenmitglieder</h3>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                                {selectedGroup.members && selectedGroup.members.length > 0 ? (
                                    selectedGroup.members.map(member => (
                                        <div key={member._id || member} className="flex items-center justify-between p-3 bg-surface-containerLow rounded-2xl border border-outlineVariant/30">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-secondary-container text-secondary-onContainer flex items-center justify-center text-sm font-bold">
                                                    {(member.userData?.scoutName?.[0] || member.userData?.firstName?.[0] || member.username?.[0] || 'M').toUpperCase()}
                                                </div>
                                                <span className="text-body-medium font-medium">{member.userData?.scoutName || member.userData?.firstName || member.username || 'Mitglied'}</span>
                                            </div>
                                            <button
                                                onClick={() => removeMemberFromGroup(member._id || member)}
                                                className="p-1.5 rounded-full hover:bg-error-container text-error transition-colors"
                                                title="Entfernen"
                                            >
                                                <X style={{ fontSize: 18 }} />
                                            </button>
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-body-small text-surface-onVariant italic py-2">Keine Mitglieder in dieser Gruppe</p>
                                )}
                            </div>
                        </div>

                        {/* Add Members */}
                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-title-small font-bold text-surface-on">Mitglieder hinzufügen</h3>
                                {selectedMemberIds.length > 0 && (
                                    <button
                                        onClick={addBatchMembersToGroup}
                                        className="md3-btn-filled text-xs px-3 py-1.5 rounded-xl font-bold flex items-center gap-1.5 animate-scale-in"
                                    >
                                        <Add style={{ fontSize: 16 }} />
                                        {selectedMemberIds.length} Ausgewählte hinzufügen
                                    </button>
                                )}
                            </div>
                            <div className="relative mb-3">
                                <Search style={{ fontSize: 18 }} className="absolute left-4 top-1/2 -translate-y-1/2 text-surface-onVariant" />
                                <input
                                    type="text"
                                    placeholder="Benutzer suchen..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="md3-textfield-outlined pl-12"
                                />
                            </div>
                            <div className="space-y-2 max-h-56 overflow-y-auto">
                                {filteredUsers
                                    .filter(u => !selectedGroup.members?.some(m => (m._id || m) === u._id))
                                    .slice(0, 15)
                                    .map(u => {
                                        const isSelected = selectedMemberIds.includes(u._id);
                                        const birthYear = u.userData?.birthDate ? new Date(u.userData.birthDate).getFullYear() : null;

                                        return (
                                            <div
                                                key={u._id}
                                                className={`flex items-center justify-between p-3 rounded-2xl transition-colors border ${
                                                    isSelected ? 'bg-primary-container/20 border-primary/40' : 'hover:bg-surface-containerLow border-outlineVariant/20'
                                                }`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => {
                                                            setSelectedMemberIds(prev =>
                                                                isSelected ? prev.filter(id => id !== u._id) : [...prev, u._id]
                                                            );
                                                        }}
                                                        className="w-4 h-4 accent-primary rounded cursor-pointer"
                                                    />
                                                    <div className="w-8 h-8 rounded-full bg-primary-container text-primary-onContainer flex items-center justify-center text-sm font-bold">
                                                        {(u.userData?.scoutName?.[0] || u.userData?.firstName?.[0] || u.username?.[0] || '?').toUpperCase()}
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-body-medium font-medium">{u.userData?.scoutName || u.userData?.firstName || u.username || 'Benutzer'}</span>
                                                            {birthYear && (
                                                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-surface-containerHigh text-surface-onVariant">
                                                                    Jg. {birthYear}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <span className="text-xs text-surface-onVariant">{u.email}</span>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => addMemberToGroup(u._id)}
                                                    className="md3-btn-tonal text-xs px-3 py-1.5 rounded-xl flex items-center gap-1"
                                                >
                                                    <Add style={{ fontSize: 16 }} />
                                                    Hinzufügen
                                                </button>
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminGroups;
